import React, { useCallback, useMemo } from 'react';
import * as Styles from './Services.styles';
import { Title } from '../NewHomeLayoutHandler/NewHomeLayoutHandler.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { SERVICES_IDS } from './Services.enum';
import { IconArrow, IconFolderOutline, IconMail } from 'gatorcomponents';
import hostingIllustration from '@/media/home/home_banner_ad_background.png';
import openClawIllustration from '@/media/home/home_banner_openclaw.png';
import snappyIllustration from '@/media/home/home_banner_snappy_monarx.png';
import emailIllustration from '@/media/home/home_banner_sites.png';

const CARDS = {
  [SERVICES_IDS.HOSTING]: {
    id: SERVICES_IDS.HOSTING,
    variant: 'hosting',
    alt: 'card-hosting',
  },
  [SERVICES_IDS.GATOR_PROTECT]: {
    id: SERVICES_IDS.GATOR_PROTECT,
    variant: 'gator-protect',
    alt: 'card-gator-protect',
  },
  [SERVICES_IDS.OPEN_CLAW]: {
    id: SERVICES_IDS.OPEN_CLAW,
    variant: 'open-claw',
    alt: 'card-open-claw',
  },
  [SERVICES_IDS.EMAIL]: {
    id: SERVICES_IDS.EMAIL,
    variant: 'email',
    alt: 'card-email',
  },
};

const renderTitle = ({ title, fallback }) => {
  if (!title) {
    return fallback;
  }

  const prefixNeedsSpace = title.prefix && !/\s$/.test(title.prefix);

  return (
    <>
      {title.prefix && (
        <>
          {title.prefix}
          {prefixNeedsSpace && ' '}
        </>
      )}
      {title.highlight && (
        <Styles.CardTitleHighlight>{title.highlight}</Styles.CardTitleHighlight>
      )}
      {title.suffix && (
        <>
          {' '}
          {title.suffix}
        </>
      )}
    </>
  );
};

const renderIllustration = ({ variant, locale }) => {
  switch (variant) {
    case 'hosting':
      return (
        <Styles.HostingIllustration
          src={hostingIllustration}
          alt=""
          aria-hidden="true"
        />
      );

    case 'gator-protect':
      return (
        <Styles.GatorProtectIllustration>
          <Styles.SnappyIllustration
            src={snappyIllustration}
            aria-hidden="true"
          />
          <Styles.SecurityMetrics>
            {locale?.metrics?.map(metric => (
              <Styles.SecurityMetric key={metric.label}>
                <Styles.SecurityMetricLabel>
                  <IconFolderOutline color="lowDark" />
                  {metric.label}
                </Styles.SecurityMetricLabel>
                <Styles.SecurityMetricValue>{metric.value}</Styles.SecurityMetricValue>
              </Styles.SecurityMetric>
            ))}
          </Styles.SecurityMetrics>
        </Styles.GatorProtectIllustration>
      );

    case 'open-claw':
      return (
        <>
          <Styles.OpenClawGlow />
          <Styles.OpenClawIllustration>
            <Styles.OpenClawImage
              src={openClawIllustration}
              alt=""
              aria-hidden="true"
            />
          </Styles.OpenClawIllustration>
        </>
      );

    case 'email':
      return (
        <Styles.EmailIllustration>
          <Styles.EmailImageContainer>
            <Styles.EmailBannerImage
              src={emailIllustration}
              alt=""
              aria-hidden="true"
            />
            {locale?.emailExample && (
              <Styles.EmailAddressChip>
                <IconMail color="primaryBlue" size="sm" />
                <Styles.EmailAddressText>{locale.emailExample}</Styles.EmailAddressText>
              </Styles.EmailAddressChip>
            )}
          </Styles.EmailImageContainer>
        </Styles.EmailIllustration>
      );

    default:
      return null;
  }
};

const Services = ({
  servicesLocale,
  cardsActions = {},
  hasHosting,
}) => {
  const cardsLocale = servicesLocale?.cards;

  const filteredCards = useMemo(
    () => (hasHosting
      ? Object.values(CARDS).filter(card => card.id !== SERVICES_IDS.HOSTING)
      : Object.values(CARDS).filter(card => card.id !== SERVICES_IDS.GATOR_PROTECT)
    ),
    [hasHosting],
  );

  const handleCardClick = useCallback(
    (cardId) => {
      const action = cardsActions[cardId];
      if (action?.onClick) {
        action.onClick();
      }
    },
    [cardsActions],
  );

  return (
    <Styles.Wrapper data-testid="services-wrapper">
      <Styles.Header>
        <Title>{servicesLocale.title}</Title>
      </Styles.Header>

      <Styles.CardsWrapper>
        {filteredCards.map((card, index) => {
          const cardLocale = cardsLocale?.[card.id];
          const cardLabel = cardLocale?.alt ?? card.alt;
          const isFeaturedHostingCard = index === 0 && card.variant === 'hosting';
          const ctaIconSize = isFeaturedHostingCard ? 'md' : 'sm';

          return (
            <Styles.CardWrapper key={card.alt} $featured={index === 0}>
              <Styles.Card
                $featured={index === 0}
                $variant={card.variant}
                aria-label={cardLabel}
                data-testid={`services-card-${card.id}`}
                onClick={() => handleCardClick(card.id)}
                type="button"
              >
                {cardLocale?.showTag && (
                  <Styles.Tag>{cardLocale?.tag}</Styles.Tag>
                )}

                <Styles.CardContent $featured={index === 0} $variant={card.variant}>
                  <Styles.CardTextGroup $featured={index === 0} $variant={card.variant}>
                    <Styles.CardTitle $featured={index === 0} $variant={card.variant}>
                      {renderTitle({
                        title: cardLocale?.title,
                        fallback: cardLabel,
                      })}
                    </Styles.CardTitle>

                    {cardLocale?.cta && (
                      <Styles.CardCta $featured={index === 0} $variant={card.variant}>
                        {cardLocale.cta}
                        <IconArrow direction="right" color="lowDark" size={ctaIconSize} />
                      </Styles.CardCta>
                    )}
                  </Styles.CardTextGroup>

                  <Styles.CardIllustration $featured={index === 0} $variant={card.variant}>
                    {renderIllustration({ variant: card.variant, locale: cardLocale })}
                  </Styles.CardIllustration>
                </Styles.CardContent>
              </Styles.Card>
            </Styles.CardWrapper>
          );
        })}
      </Styles.CardsWrapper>
    </Styles.Wrapper>
  );
};

export default withErrorBoundary(Services, 'Services');
